import { supabase } from "./supabase";

export interface OnboardingData {
  name?: string;
  dateOfBirth?: string;
  dateOfDeath?: string;
  funeralHome?: string;
  placeOfLastResidence?: string;
  yourName?: string;
  phoneNumber?: string;
}

export interface UserData {
  auth_id: string;
  email: string;
  phone_number?: string;
  full_name?: string;
  first_name?: string;
  last_name?: string;
  completed_onboarding?: boolean;
  last_active_estate_id?: number;
  customizations?: Record<string, string | number | boolean>;
}

export interface PersonData {
  owner_user_id?: number;
  full_name?: string;
  first_name?: string;
  last_name?: string;
  date_of_birth?: string;
  date_of_death?: string;
  funeral_home?: string;
  location?: string;
  main_image_url?: string;
  estate_type?: string;
}

export const saveUserToDatabase = async (userData: UserData) => {
  try {
    const { data, error } = await supabase.from("users").insert([userData]).select();

    if (error) {
      console.error("Error saving user to database:", error);
      throw error;
    }

    console.log("User saved to database:", data);
    return data;
  } catch (error) {
    console.error("Database error:", error);
    throw error;
  }
};

export const savePersonToDatabase = async (personData: PersonData) => {
  try {
    const { data, error } = await supabase.from("estates").insert([personData]).select();

    if (error) {
      console.error("Error saving person to database:", error);
      throw error;
    }

    console.log("Person saved to database:", data);
    return data;
  } catch (error) {
    console.error("Database error:", error);
    throw error;
  }
};

export const saveUserPersonRelationship = async (userId: number, estateId: number) => {
  try {
    const { data, error } = await supabase
      .from("user_estates")
      .insert([{ user_id: userId, estate_id: estateId }])
      .select();

    if (error) {
      console.error("Error saving user-person relationship:", error);
      throw error;
    }

    console.log("User-person relationship saved:", data);
    return data;
  } catch (error) {
    console.error("Database error:", error);
    throw error;
  }
};

export const processOnboardingData = (
  onboardingData: OnboardingData,
  authId: string,
  email: string,
  lastActivePersonId?: number,
): UserData => {
  // Split full name into first and last name
  const fullName = onboardingData.yourName || "";
  const nameParts = fullName.trim().split(" ");
  const firstName = nameParts[0] || "";
  const lastName = nameParts.slice(1).join(" ") || "";

  return {
    auth_id: authId,
    email: email,
    phone_number: onboardingData.phoneNumber || undefined,
    full_name: fullName || undefined,
    first_name: firstName || undefined,
    last_name: lastName || undefined,
    completed_onboarding: true,
    last_active_estate_id: lastActivePersonId,
  };
};

export const processDeceasedPersonData = (
  onboardingData: OnboardingData,
  ownerUserId: number,
): PersonData => {
  // Split deceased person's name into first and last name
  const fullName = onboardingData.name || "";
  const nameParts = fullName.trim().split(" ");
  const firstName = nameParts[0] || "";
  const lastName = nameParts.slice(1).join(" ") || "";

  return {
    owner_user_id: ownerUserId,
    full_name: fullName || undefined,
    first_name: firstName || undefined,
    last_name: lastName || undefined,
    date_of_birth: onboardingData.dateOfBirth || undefined,
    date_of_death: onboardingData.dateOfDeath || undefined,
    funeral_home: onboardingData.funeralHome || undefined,
    location: onboardingData.placeOfLastResidence || undefined,
    main_image_url: undefined, // Will be set later when image is uploaded
    estate_type: "deceased",
  };
};

export const fetchUserData = async (authId: string): Promise<UserData | null> => {
  try {
    const { data: userData, error: userError } = await supabase
      .from("users")
      .select("*")
      .eq("auth_id", authId)
      .single();

    if (userError) {
      if (userError.code === "PGRST116") {
        // No rows found - user not found in database
        return null;
      }
      console.error("Error fetching user data:", userError);
      throw userError;
    }

    return userData;
  } catch (error) {
    console.error("Database error:", error);
    throw error;
  }
};

export const fetchPersonData = async (authId: string): Promise<PersonData | null> => {
  try {
    // First get the user's database record to get the user_id
    const { data: userData, error: userError } = await supabase
      .from("users")
      .select("user_id")
      .eq("auth_id", authId)
      .single();

    if (userError) {
      if (userError.code === "PGRST116") {
        // No rows found - user not found in database
        return null;
      }
      console.error("Error fetching user data:", userError);
      throw userError;
    }

    if (!userData?.user_id) {
      return null;
    }

    // Then get the user's person relationship
    const { data: userPersonData, error: userPersonError } = await supabase
      .from("user_estates")
      .select("estate_id")
      .eq("user_id", userData.user_id)
      .single();

    if (userPersonError) {
      if (userPersonError.code === "PGRST116") {
        // No rows found - user has no associated person
        return null;
      }
      console.error("Error fetching user-person relationship:", userPersonError);
      throw userPersonError;
    }

    if (!userPersonData?.estate_id) {
      return null;
    }

    // Finally get the person data
    const { data: personData, error: personError } = await supabase
      .from("estates")
      .select("*")
      .eq("estate_id", userPersonData.estate_id)
      .single();

    if (personError) {
      console.error("Error fetching person data:", personError);
      throw personError;
    }

    return personData;
  } catch (error) {
    console.error("Database error:", error);
    throw error;
  }
};

export const updateUserCustomizations = async (
  authId: string,
  customizations: Record<string, unknown>,
): Promise<UserData | null> => {
  try {
    const { data, error } = await supabase
      .from("users")
      .update({ customizations })
      .eq("auth_id", authId)
      .select()
      .single();

    if (error) {
      console.error("Error updating user customizations:", error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error("Database error:", error);
    throw error;
  }
};
